import { useState } from 'react';
import PropTypes from 'prop-types';
import Chip from 'Components/Molecules/Chip/Chip';
import { cn } from 'Utils/cn';
import { cva } from 'class-variance-authority';
import FloatLabel from 'Components/Atoms/FloatLabel/FloatLabel';
import ErrorMessage from 'Components/Atoms/ErrorMessage/ErrorMessage';

const inputVariants = cva(
    'flex-wrap flex gap-1 py-2 items-center w-full flex w-full text-dark-blue rounded-md border border-gray-200 bg-white px-3.5 shadow-sm transition-all placeholder:text-dark-blue/50 group-focus-within:outline-none group-focus-within:ring-2 ring-primary/20 group-focus-within:border-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
    {
        variants: {
            size: {
                normal: 'min-h-11',
                small: 'h-8 md:text-xs',
            },
            status: {
                error: 'border-danger',
                highlight:
                    'border-warning ring-2 border-warning group-focus-within:ring-2 ring-warning/20 group-focus-within:border-warning',
                default: '',
            },
        },
        defaultVariants: {
            size: 'normal',
            status: 'default',
        },
    },
);

export default function InputTags(props) {
    const { className, tags = [], onChange, placeholder, disabled, label, error, highlight, minLength = 3 } = props;
    const [inputValue, setInputValue] = useState('');

    const hasError = !!error;

    const [isHighlighted, setIsHighlighted] = useState(highlight || false);
    const status = isHighlighted ? 'highlight' : hasError ? 'error' : 'default';

    const handleKeyDown = (ev) => {
        const { code } = ev;

        if (code === 'Backspace' && tags.length && inputValue === '') {
            ev.preventDefault();

            const newTags = [...tags];
            const poppedTag = newTags.pop();
            onChange(newTags);
            setInputValue(poppedTag);
            return;
        }

        if (code === 'Enter') {
            ev.preventDefault();
            const value = inputValue.trim();
            if (value.length < minLength || tags.includes(value)) return;
            onChange([...tags, value]);
            setInputValue('');
            return;
        }

        const value = inputValue.trim();

        if (tags.includes(value)) return;
        if (value === '') return;

        const lastChar = value[value.length - 1];

        if (code === 'Space' && lastChar === ',') {
            ev.preventDefault();
            const valueWithoutComma = value.slice(0, -1).trim();
            if (valueWithoutComma.length < minLength) {
                setInputValue('');
                return;
            }
            onChange([...tags, valueWithoutComma]);
            setInputValue('');
        }
    };

    const handleChange = (ev) => {
        setInputValue(ev.target.value);
    };

    return (
        <div className={cn('relative w-full group', className)}>
            <FloatLabel label={label} hasError={hasError} highlight={isHighlighted} />

            <div className={inputVariants({ className, status })}>
                {tags.map((tag, index) => (
                    <Chip key={index} onDelete={() => onChange(tags.filter((_, i) => i !== index))}>
                        {tag}
                    </Chip>
                ))}

                <input
                    disabled={disabled}
                    className="grow border-0 outline-0 bg-transparent text-current"
                    type="text"
                    placeholder={placeholder}
                    value={inputValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onBlur={() => {
                        setIsHighlighted(false);
                        const trimmed = inputValue.trim();
                        if (trimmed.length < minLength) {
                            setInputValue('');
                            return;
                        }
                        onChange([...tags, trimmed]);
                        setInputValue('');
                    }}
                />
            </div>
            <ErrorMessage message={error} />
        </div>
    );
}

InputTags.propTypes = {
    onChange: PropTypes.func.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    label: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    highlight: PropTypes.bool,
    minLength: PropTypes.number,
};
